import { useState, useCallback, useEffect } from 'react';
import { useExpenses } from '../contexts/ExpenseContext';
import { useMode } from '../contexts/ModeContext';
import { 
  CATEGORIES, 
  FAMILY_CATEGORIES,
  FUEL_SUBCATEGORIES,
  TRUCK_SUBCATEGORIES,
  ExpenseCategory, 
  FamilyCategory,
  FuelSubcategory,
  TruckSubcategory,
  getLocalDate,
  checkForDuplicates as smartDuplicateCheck,
  formatDuplicateWarning,
} from '../types';
import { receiptExtract } from '../services/api';
import Tesseract from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite URL import for worker
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { 
  Upload as UploadIcon, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  AlertTriangle,
  Loader2,
  Sparkles,
  Edit3,
  Eye,
  Save,
  X,
  RotateCcw
} from 'lucide-react';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default function SmartUpload() {
  const { addExpense, expenses } = useExpenses();
  const { isFamily } = useMode();
  const visibleCategories = isFamily ? FAMILY_CATEGORIES : CATEGORIES;

  // File state
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  
  // OCR state
  const [ocrText, setOcrText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  
  // Form state - editable fields
  const [category, setCategory] = useState<ExpenseCategory | FamilyCategory>(isFamily ? 'provisions' : 'fuel');
  useEffect(() => {
    if (isFamily && !FAMILY_CATEGORIES.some(c => c.value === category)) {
      setCategory('provisions');
    } else if (!isFamily && !CATEGORIES.some(c => c.value === category)) {
      setCategory('fuel');
    }
  }, [isFamily, category]);
  const [fuelSubcategory, setFuelSubcategory] = useState<FuelSubcategory>('delivery');
  const [vendor, setVendor] = useState('');
  const [storeNumber, setStoreNumber] = useState('');
  const [date, setDate] = useState(getLocalDate());
  const [amount, setAmount] = useState('');
  const [gallons, setGallons] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [description, setDescription] = useState('');
  const [subcategory, setSubcategory] = useState<string>('');

  // AI Vision extract result (flag_for_review, flag_reason, confidence)
  const [smartExtractResult, setSmartExtractResult] = useState<{
    flag_for_review: boolean;
    flag_reason: string | null;
    confidence: number | null;
  } | null>(null);
  const [isExtractingVision, setIsExtractingVision] = useState(false);

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Smart field extraction from OCR text
  const extractFieldsFromText = useCallback((text: string) => {
    console.log('=== SMART FIELD EXTRACTION ===');
    console.log('Text length:', text.length);
    
    // Extract vendor/brand
    const vendorPatterns = [
      { pattern: /Love'?s|LOVES/i, vendor: "Love's" },
      { pattern: /Pilot|Flying\s*J/i, vendor: 'Pilot' },
      { pattern: /CHEVRON|TEXACO/i, vendor: 'Chevron' },
      { pattern: /TA\s*Petro|TravelCenters/i, vendor: 'TA Petro' },
      { pattern: /Sunoco/i, vendor: 'Sunoco' },
      { pattern: /Shell/i, vendor: 'Shell' },
      { pattern: /BP/i, vendor: 'BP' },
      { pattern: /Exxon|Mobil/i, vendor: 'Exxon' },
      { pattern: /Casey'?s/i, vendor: "Casey's" },
      { pattern: /QuikTrip|QT/i, vendor: 'QuikTrip' },
      { pattern: /Kum\s*&\s*Go/i, vendor: 'Kum & Go' },
      { pattern: /Speedway/i, vendor: 'Speedway' },
      { pattern: /Murphy/i, vendor: 'Murphy' },
      { pattern: /Racetrac/i, vendor: 'RaceTrac' },
      { pattern: /Wawa/i, vendor: 'Wawa' },
      { pattern: /Sheetz/i, vendor: 'Sheetz' },
      { pattern: /DONCO/i, vendor: 'Chevron' }, // DONCO is a Chevron dealer
    ];
    
    for (const { pattern, vendor: v } of vendorPatterns) {
      if (pattern.test(text)) {
        setVendor(v);
        console.log('Detected vendor:', v);
        break;
      }
    }
    
    // Extract store number
    const storeMatch = text.match(/(?:STORE|Stn|Station)\s*#?\s*(\d{2,8})/i) ||
                      text.match(/(?:DONCO|Location)\s*#?\s*(\d+)/i) ||
                      text.match(/#(\d{3,6})\b/);
    if (storeMatch) {
      setStoreNumber(storeMatch[1]);
      console.log('Detected store:', storeMatch[1]);
    }
    
    // Extract date - multiple formats
    const datePatterns = [
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/,  // MM/DD/YYYY
      /(\d{1,2})\/(\d{1,2})\/(\d{2})\b/, // MM/DD/YY
      /(\d{4})-(\d{2})-(\d{2})/,         // YYYY-MM-DD
    ];
    
    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        let year, month, day;
        if (pattern.source.startsWith('(\\d{4})')) {
          // YYYY-MM-DD format
          [, year, month, day] = match;
        } else {
          [, month, day, year] = match;
          // Handle 2-digit year
          if (year.length === 2) {
            year = parseInt(year) > 50 ? `19${year}` : `20${year}`;
          }
        }
        const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        setDate(formattedDate);
        console.log('Detected date:', formattedDate);
        break;
      }
    }
    
    // Extract amount - look for Total, TOTAL, or large dollar amounts
    const amountPatterns = [
      /(?:Total|TOTAL)[:\s]*\$?\s*([\d,]+\.?\d{0,2})/i,
      /(?:Amount|AMOUNT)[:\s]*\$?\s*([\d,]+\.?\d{0,2})/i,
      /(?:DEBIT|Credit)\s*\$?\s*([\d,]+\.?\d{2})/i,
      /\$\s*([\d,]+\.\d{2})/, // Generic dollar amount
    ];
    
    for (const pattern of amountPatterns) {
      const match = text.match(pattern);
      if (match) {
        const amt = parseFloat(match[1].replace(/,/g, ''));
        if (amt > 5 && amt < 1000) { // Reasonable fuel amount range
          setAmount(amt.toFixed(2));
          console.log('Detected amount:', amt);
          break;
        }
      }
    }
    
    // Extract gallons
    const gallonsMatch = text.match(/(?:Gallons?|Gal)[:\s]*([\d.]+)/i) ||
                        text.match(/([\d.]+)\s*(?:GAL|G)\s*@/i) ||
                        text.match(/(?:DSL|DIESEL)[^@]*?([\d.]+)\s/i);
    if (gallonsMatch) {
      const gal = parseFloat(gallonsMatch[1]);
      if (gal > 1 && gal < 200) {
        setGallons(gal.toFixed(3));
        console.log('Detected gallons:', gal);
      }
    }
    
    // Extract transaction/receipt number
    const transMatch = text.match(/(?:Transaction|Tkt|Ticket|Invoice|Inv|Receipt|Auth)\s*#?[:\s]*(\d{4,})/i);
    if (transMatch) {
      setTransactionId(transMatch[1]);
      console.log('Detected transaction:', transMatch[1]);
    }
    
    // Extract card last 4
    const cardMatch = text.match(/\*{3,}(\d{4})/);
    if (cardMatch) {
      setCardLast4(cardMatch[1]);
      console.log('Detected card:', cardMatch[1]);
    }
    
    // Build description from location if found
    const locationMatch = text.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*([A-Z]{2})\s+\d{5}/);
    if (locationMatch) {
      setDescription(`${locationMatch[1]}, ${locationMatch[2]}`);
      console.log('Detected location:', locationMatch[1], locationMatch[2]);
    }
  }, []);

  // Convert PDF page to image for OCR
  const pdfToImage = async (pdfBlob: Blob): Promise<Blob | null> => {
    try {
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      
      const scale = 2;
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return null;
      
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      });
    } catch (e) {
      console.error('PDF to image conversion failed:', e);
      return null;
    }
  };

  // Extract text from PDF using PDF.js
  const extractPdfText = async (pdfBlob: Blob): Promise<string> => {
    try {
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        fullText += pageText + '\n';
      }
      
      return fullText;
    } catch (e) {
      console.error('PDF text extraction failed:', e);
      return '';
    }
  };

  // Process uploaded file with OCR
  const processFile = async (uploadedFile: File) => {
    setIsProcessing(true);
    setOcrProgress(0);
    setError('');
    
    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(uploadedFile);
      setFilePreview(previewUrl);
      
      let extractedText = '';
      
      if (uploadedFile.type === 'application/pdf') {
        // Try PDF.js text extraction first
        setOcrProgress(10);
        extractedText = await extractPdfText(uploadedFile);
        
        // If PDF text extraction got good results, use that
        if (extractedText.trim().length > 50) {
          console.log('Using PDF.js text extraction');
          setOcrProgress(100);
        } else {
          // Fall back to OCR via image conversion
          console.log('PDF text extraction weak, falling back to OCR');
          setOcrProgress(20);
          const imageBlob = await pdfToImage(uploadedFile);
          if (imageBlob) {
            const result = await Tesseract.recognize(
              imageBlob,
              'eng',
              {
                logger: (m) => {
                  if (m.status === 'recognizing text') {
                    setOcrProgress(20 + Math.round(m.progress * 80));
                  }
                }
              }
            );
            extractedText = result.data.text;
          }
        }
      } else {
        // Use Tesseract OCR for images
        const result = await Tesseract.recognize(
          uploadedFile,
          'eng',
          {
            logger: (m) => {
              if (m.status === 'recognizing text') {
                setOcrProgress(Math.round(m.progress * 100));
              }
            }
          }
        );
        extractedText = result.data.text;
      }
      
      setOcrText(extractedText);
      
      // Auto-extract fields from the text
      if (extractedText.trim().length > 20) {
        extractFieldsFromText(extractedText);
      }
      
    } catch (e) {
      console.error('OCR processing failed:', e);
      setError('Failed to process file. Try a clearer image or PDF.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      processFile(selectedFile);
    }
  }, []);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      processFile(droppedFile);
    }
  }, []);

  // Map receipt-extract API category string to ExpenseCategory
  const mapApiCategoryToExpense = (apiCategory: string | null): ExpenseCategory | FamilyCategory => {
    if (!apiCategory) return isFamily ? 'provisions' : 'fuel';
    const lower = apiCategory.toLowerCase();
    if (lower.includes('fuel')) return 'fuel';
    if (lower.includes('truck') || lower.includes('vehicle')) return 'truck';
    if (lower.includes('travel') || lower.includes('repositioning')) return 'travel';
    if (lower.includes('settlement')) return 'settlements';
    if (lower.includes('maintenance') || lower.includes('equipment')) return 'truck';
    if (lower.includes('toll') || lower.includes('fee')) return 'tolls';
    if (lower.includes('meal')) return 'meals';
    if (lower.includes('office')) return 'office';
    return 'miscellaneous';
  };

  // Extract with AI Vision (receipt-extract API) and populate form
  const handleExtractWithVision = useCallback(async () => {
    if (!file) {
      setError('Please upload a file first');
      return;
    }
    setIsExtractingVision(true);
    setError('');
    setSmartExtractResult(null);
    try {
      let imageBlob: Blob = file;
      let filename = file.name;
      if (file.type === 'application/pdf') {
        const blob = await pdfToImage(file);
        if (!blob) {
          setError('Could not convert PDF to image for AI extraction.');
          return;
        }
        imageBlob = blob;
        filename = 'page1.png';
      }
      const result = await receiptExtract(imageBlob, filename);
      setVendor(result.vendor ?? '');
      setDate(result.date ?? getLocalDate());
      setAmount(result.amount != null ? String(result.amount) : '');
      setCategory(mapApiCategoryToExpense(result.category));
      setSubcategory(result.subcategory ?? '');
      setGallons(result.gallons != null ? String(result.gallons) : '');
      setSmartExtractResult({
        flag_for_review: result.flag_for_review,
        flag_reason: result.flag_reason,
        confidence: result.confidence,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI extraction failed');
    } finally {
      setIsExtractingVision(false);
    }
  }, [file]);

  // Clear and reset
  const handleClear = () => {
    setFile(null);
    setFilePreview(null);
    setOcrText('');
    setVendor('');
    setStoreNumber('');
    setDate(getLocalDate());
    setAmount('');
    setGallons('');
    setTransactionId('');
    setCardLast4('');
    setDescription('');
    setSubcategory('');
    setSmartExtractResult(null);
    setError('');
    setDuplicateWarning(null);
  };

  // Save the expense
  const handleSave = async () => {
    if (!file) {
      setError('Please upload a file first');
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    // Check for duplicates
    const dupeResult = smartDuplicateCheck(
      {
        amount: parseFloat(amount),
        date,
        description: description || `${vendor} ${storeNumber ? '#' + storeNumber : ''}`.trim(),
        vendor,
        storeNumber,
        cardLast4,
        transactionId,
        category,
      },
      expenses
    );
    
    if (dupeResult.isDuplicate) {
      setDuplicateWarning(formatDuplicateWarning(dupeResult));
      return;
    }
    
    setIsUploading(true);
    setError('');
    
    try {
      // Build full description
      let fullDescription = vendor;
      if (storeNumber) fullDescription += ` #${storeNumber}`;
      if (description) fullDescription += ` - ${description}`;
      if (gallons) fullDescription += ` (${gallons} gal)`;
      
      await addExpense({
        category,
        subcategory: category === 'fuel' ? fuelSubcategory : category === 'truck' && TRUCK_SUBCATEGORIES.some((s) => s.value === subcategory) ? (subcategory as TruckSubcategory) : undefined,
        description: fullDescription,
        amount: parseFloat(amount),
        date,
        file,
        filename: file.name,
        source: 'scanned',
        vendor: vendor || undefined,
        storeNumber: storeNumber || undefined,
        cardLast4: cardLast4 || undefined,
        transactionId: transactionId || undefined,
      });
      
      // Show success briefly, then reset for next upload
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        handleClear();
      }, 1500);
    } catch (err) {
      setError('Failed to save expense. Please try again.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  // Force save (bypass duplicate check)
  const handleForceSave = async () => {
    setDuplicateWarning(null);
    
    setIsUploading(true);
    try {
      let fullDescription = vendor;
      if (storeNumber) fullDescription += ` #${storeNumber}`;
      if (description) fullDescription += ` - ${description}`;
      if (gallons) fullDescription += ` (${gallons} gal)`;
      
      await addExpense({
        category,
        subcategory: category === 'fuel' ? fuelSubcategory : category === 'truck' && TRUCK_SUBCATEGORIES.some((s) => s.value === subcategory) ? (subcategory as TruckSubcategory) : undefined,
        description: fullDescription,
        amount: parseFloat(amount),
        date,
        file: file!,
        filename: file!.name,
        source: 'scanned',
        vendor: vendor || undefined,
        storeNumber: storeNumber || undefined,
        cardLast4: cardLast4 || undefined,
        transactionId: transactionId || undefined,
      });
      
      // Show success briefly, then reset for next upload
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        handleClear();
      }, 1500);
    } catch (err) {
      setError('Failed to save expense.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Success Toast */}
      {success && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="flex items-center gap-3 px-6 py-4 bg-green-500/90 backdrop-blur-md text-white rounded-xl shadow-2xl">
            <CheckCircle className="w-6 h-6" />
            <span className="font-semibold">Expense Saved! Ready for next receipt...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-yellow-400" />
          Smart OCR Upload
        </h1>
        <p className="text-shuttle-sky mt-1">
          Upload any receipt - OCR extracts the text, you verify the fields
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Duplicate Warning */}
      {duplicateWarning && (
        <div className="p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-xl">
          <div className="flex items-start gap-2 text-yellow-300 mb-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Possible Duplicate Found</p>
              <p className="mt-1 text-sm whitespace-pre-line">{duplicateWarning}</p>
            </div>
          </div>
          <div className="flex gap-3 ml-7">
            <button
              onClick={handleForceSave}
              className="px-4 py-2 bg-yellow-500/30 text-yellow-200 rounded-lg hover:bg-yellow-500/40 text-sm font-medium"
            >
              Save Anyway
            </button>
            <button
              onClick={() => setDuplicateWarning(null)}
              className="px-4 py-2 bg-white/10 text-white/70 rounded-lg hover:bg-white/20 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left: Upload & OCR Preview */}
        <div className="space-y-4">
          {/* Dropzone */}
          {!file ? (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-white/30 rounded-2xl p-8 text-center cursor-pointer hover:border-yellow-400/50 hover:bg-yellow-500/5 transition-all"
            >
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                id="smart-upload"
                accept="image/*,.pdf"
              />
              <label htmlFor="smart-upload" className="cursor-pointer">
                <UploadIcon className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-white">Drop receipt here</p>
                <p className="text-sm text-white/50 mt-1">or click to browse • PDF, JPG, PNG</p>
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File info & clear button */}
              <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-yellow-400" />
                  <span className="text-white text-sm truncate max-w-[200px]">{file.name}</span>
                </div>
                <button onClick={handleClear} className="p-1 hover:bg-white/10 rounded">
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>

              {/* Processing indicator */}
              {isProcessing && (
                <div className="p-4 bg-yellow-500/10 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
                    <span className="text-yellow-300">Processing OCR...</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 transition-all duration-300"
                      style={{ width: `${ocrProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* OCR Text Preview */}
              {ocrText && (
                <div className="bg-slate-900/50 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border-b border-white/10">
                    <Eye className="w-4 h-4 text-white/60" />
                    <span className="text-sm text-white/60">Extracted Text</span>
                  </div>
                  <div className="p-4 max-h-[400px] overflow-y-auto">
                    <pre className="text-xs text-white/80 whitespace-pre-wrap font-mono">
                      {ocrText}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Editable Form */}
        <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-4">
          <div className="flex items-center justify-between gap-2 text-white mb-4">
            <div className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-yellow-400" />
              <h3 className="font-semibold">Edit Fields</h3>
            </div>
            <button
              type="button"
              onClick={handleExtractWithVision}
              disabled={!file || isExtractingVision}
              className="flex items-center gap-2 px-3 py-2 bg-sky-500/80 hover:bg-sky-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isExtractingVision ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {isExtractingVision ? 'Extracting...' : 'Extract with AI Vision'}
            </button>
          </div>

          {/* Warning banner when flag_for_review */}
          {smartExtractResult?.flag_for_review && (
            <div className="flex items-start gap-2 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-200">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{smartExtractResult.flag_reason ?? 'Please review this extraction.'}</p>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm text-white/60 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
            >
              {visibleCategories.map((cat) => (
                <option key={cat.value} value={cat.value} className="bg-slate-800">
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Fuel subcategory */}
          {category === 'fuel' && (
            <div>
              <label className="block text-sm text-white/60 mb-1">Fuel Type</label>
              <select
                value={fuelSubcategory}
                onChange={(e) => setFuelSubcategory(e.target.value as FuelSubcategory)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              >
                {FUEL_SUBCATEGORIES.map((sub) => (
                  <option key={sub.value} value={sub.value} className="bg-slate-800">
                    {sub.icon} {sub.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Truck subcategory (from API or manual) */}
          {category === 'truck' && (
            <div>
              <label className="block text-sm text-white/60 mb-1">Subcategory</label>
              <select
                value={TRUCK_SUBCATEGORIES.some((s) => s.value === subcategory) ? subcategory : (subcategory || 'other')}
                onChange={(e) => setSubcategory(e.target.value as TruckSubcategory)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              >
                {TRUCK_SUBCATEGORIES.map((sub) => (
                  <option key={sub.value} value={sub.value} className="bg-slate-800">
                    {sub.icon} {sub.label}
                  </option>
                ))}
                {subcategory && !TRUCK_SUBCATEGORIES.some((s) => s.value === subcategory) && (
                  <option value={subcategory} className="bg-slate-800">{subcategory}</option>
                )}
              </select>
            </div>
          )}

          {/* Subcategory text for other categories when set from API */}
          {category !== 'fuel' && category !== 'truck' && subcategory && (
            <div>
              <label className="block text-sm text-white/60 mb-1">Subcategory</label>
              <input
                type="text"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                placeholder="From AI or manual"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
          )}

          {/* Vendor & Store */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-white/60 mb-1">Vendor</label>
              <input
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="Love's, Pilot..."
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Store #</label>
              <input
                type="text"
                value={storeNumber}
                onChange={(e) => setStoreNumber(e.target.value)}
                placeholder="674"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
          </div>

          {/* Date & Amount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-white/60 mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="78.82"
                className={`w-full px-3 py-2 rounded-lg text-white placeholder-white/30 ${
                  smartExtractResult?.confidence != null && smartExtractResult.confidence < 80
                    ? 'bg-red-500/20 border-2 border-red-500'
                    : 'bg-white/10 border border-white/20'
                }`}
              />
              {smartExtractResult?.confidence != null && smartExtractResult.confidence < 80 && (
                <p className="text-xs text-red-400 mt-1">Low confidence — please double-check amount</p>
              )}
            </div>
          </div>

          {/* Gallons & Transaction ID */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-white/60 mb-1">Gallons</label>
              <input
                type="number"
                step="0.001"
                value={gallons}
                onChange={(e) => setGallons(e.target.value)}
                placeholder="30.327"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Receipt/Tkt #</label>
              <input
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="0003449"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
          </div>

          {/* Card Last 4 & Description */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-white/60 mb-1">Card Last 4</label>
              <input
                type="text"
                maxLength={4}
                value={cardLast4}
                onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, ''))}
                placeholder="8405"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Location</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Heidelberg, MS"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-4">
            {/* Clear Button */}
            <button
              onClick={handleClear}
              disabled={isUploading || !file}
              className="flex items-center justify-center gap-2 py-3 px-4 bg-white/10 border border-white/20 text-white/70 rounded-xl font-medium hover:bg-white/20 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <RotateCcw className="w-5 h-5" />
              Clear
            </button>
            
            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={isUploading || !file || !amount}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-yellow-500 to-amber-500 text-slate-900 rounded-xl font-semibold hover:from-yellow-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Expense
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

